import { useState, useEffect, useCallback } from "react";
import { supabase } from "./supabase";

export type PermissionKey =
  | "strutture"
  | "prenotazioni"
  | "ospiti"
  | "guestApp"
  | "team"
  | "finanze"
  | "operativo"
  | "pricing"
  | "impostazioni";

export type Permissions = Record<PermissionKey, boolean>;

const ADMIN_PERMISSIONS: Permissions = {
  strutture:    true,
  prenotazioni: true,
  ospiti:       true,
  guestApp:     true,
  team:         true,
  finanze:      true,
  operativo:    true,
  pricing:      true,
  impostazioni: true,
};

// ── Global store ──────────────────────────────────────────────────────────────
let _perms: Permissions = ADMIN_PERMISSIONS;
let _isAdmin: boolean = true;
let _loaded: boolean = false;
const _subs = new Set<() => void>();

function _notify() { _subs.forEach(fn => fn()); }

async function _loadFromDB() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { data } = await supabase
    .from("team_members")
    .select("permessi, ruolo")
    .eq("member_id", user.id)
    .single();

  if (data) {
    _isAdmin = false;
    _perms = { ...ADMIN_PERMISSIONS, ...data.permessi, impostazioni: false };
  } else {
    _isAdmin = true;
    _perms = ADMIN_PERMISSIONS;
  }
  _loaded = true;
  _notify();
}

supabase.auth.onAuthStateChange((event) => {
  if (event === "SIGNED_IN") { _loaded = false; _loadFromDB(); }
  if (event === "SIGNED_OUT") { _perms = ADMIN_PERMISSIONS; _isAdmin = true; _loaded = false; _notify(); }
});

export function usePermissions() {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const handler = () => forceUpdate(n => n + 1);
    _subs.add(handler);
    if (!_loaded) _loadFromDB();
    return () => { _subs.delete(handler); };
  }, []);

  const toggle = useCallback((key: PermissionKey) => {
    _perms = { ..._perms, [key]: !_perms[key] };
    _notify();
  }, []);

  const reset = useCallback(() => {
    _perms = ADMIN_PERMISSIONS;
    _notify();
  }, []);

  const canSee = useCallback((key: PermissionKey): boolean => {
    if (_isAdmin) return true;
    if (key === "impostazioni") return false;
    return _perms[key] ?? false;
  }, []);

  const updateMemberPermissions = useCallback(async (memberId: number, newPerms: Partial<Permissions>) => {
    await supabase.from("team_members").update({ permessi: newPerms }).eq("id", memberId);
  }, []);

  return {
    permissions: _perms,
    isAdmin: _isAdmin,
    toggle,
    reset,
    teamViewActive: !_isAdmin,
    setTeamViewActive: (_v: boolean) => {},
    canSee,
    updateMemberPermissions,
  };
}
